import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Logo } from '../components/ui/Logo'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import {
  ArrowLeft, Building2, Users, Palette, Shield, Crown,
  Settings, Loader2, Save, Upload, ChevronRight, UserPlus,
  Mail, Phone, MoreHorizontal, Copy, CheckCircle2, AlertCircle
} from 'lucide-react'
import { useAuth } from '../hooks/use-auth'
import { useI18n } from '../i18n'
import { get, post, put } from '../helper/network'
import API from '../helper/api'
import axios from 'axios'

interface BrandingConfig {
  enterpriseName: string
  shortCode: string
  logoUrl: string
  themePrimaryColor: string
  themeSecondaryColor: string
  themeFontFamily: string
  loginBgUrl: string
  welcomeText: string
}

interface EnterpriseUser {
  id: string
  userName: string
  nickName: string
  avatarUrl: string
  phoneNumber: string
  email: string
  department: string
  position: string
  role: string
  status: number
  tokenUsed?: number
  tokenQuota?: number
  createTime?: string
}

interface EnterpriseConfig {
  id: string
  name: string
  shortCode: string
  logoUrl: string
  industryType: string
  subscriptionTier: string
  maxUsers: number
  enterpriseTokenQuota: number
  enterpriseTokenUsed: number
  contactName: string
  contactEmail: string
  contactPhone: string
  themePrimaryColor: string
  themeSecondaryColor: string
  welcomeText: string
  status: string
  bailianWorkspaceId?: string
  expireTime?: string
  createTime?: string
}

type TabKey = 'overview' | 'branding' | 'users' | 'settings'

export default function EnterprisePage() {
  const { user, isLoggedIn, refreshUserInfo } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<TabKey>('overview')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  // 品牌配置
  const [branding, setBranding] = useState<BrandingConfig>({
    enterpriseName: '', shortCode: '', logoUrl: '',
    themePrimaryColor: '#f97316', themeSecondaryColor: '#fb923c',
    themeFontFamily: '', loginBgUrl: '', welcomeText: '',
  })

  // 企业配置
  const [config, setConfig] = useState<EnterpriseConfig | null>(null)

  // 用户列表
  const [users, setUsers] = useState<EnterpriseUser[]>([])
  const [roleUpdating, setRoleUpdating] = useState<string | null>(null)
  const [userActionId, setUserActionId] = useState<string | null>(null)

  // 添加成员
  const [showAddMember, setShowAddMember] = useState(false)
  const [addMemberUserId, setAddMemberUserId] = useState('')
  const [addMemberLoading, setAddMemberLoading] = useState(false)
  const [addMemberMsg, setAddMemberMsg] = useState('')

  // 企业额度
  const [quota, setQuota] = useState<{ total: number; used: number; remaining: number; planCode?: string; planExpireTime?: string } | null>(null)

  // logo上传
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  // 设置表单
  const [settingsForm, setSettingsForm] = useState({
    name: '', industryType: '', contactName: '', contactEmail: '', contactPhone: '',
  })

  // 复制短码提示
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!isLoggedIn) { navigate('/login'); return }
    loadData()
  }, [isLoggedIn])

  const loadData = async () => {
    setLoading(true)
    await Promise.all([loadBranding(), loadConfig(), loadUsers(), loadQuota()])
    setLoading(false)
  }

  const loadBranding = async () => {
    try {
      const res: any = await get(API.GET_ENTERPRISE_BRANDING, {})
      if (res && res.code === 200 && res.data) {
        setBranding(res.data)
      }
    } catch {}
  }

  const loadConfig = async () => {
    try {
      const res: any = await get(API.GET_ENTERPRISE_CONFIG, {})
      if (res && res.code === 200 && res.data) {
        setConfig(res.data)
        setSettingsForm({
          name: res.data.name || '',
          industryType: res.data.industryType || '',
          contactName: res.data.contactName || '',
          contactEmail: res.data.contactEmail || '',
          contactPhone: res.data.contactPhone || '',
        })
      }
    } catch {}
  }

  const loadUsers = async () => {
    try {
      const res: any = await get(API.GET_ENTERPRISE_USERS, {})
      if (res && res.code === 200 && res.data) {
        setUsers(Array.isArray(res.data) ? res.data : [])
      }
    } catch {}
  }

  const loadQuota = async () => {
    try {
      const res: any = await get(API.GET_ENTERPRISE_QUOTA, {})
      if (res && res.code === 200 && res.data) {
        setQuota(res.data)
      }
    } catch {}
  }

  const handleSaveBranding = async () => {
    setSaving(true); setError(''); setSaved(false)
    try {
      const res: any = await put(API.PUT_ENTERPRISE_BRANDING, branding)
      if (res && res.code === 200) { setSaved(true) }
      else { setError(res?.message || t('保存失败', 'Save failed')) }
    } catch { setError(t('网络错误', 'Network error')) }
    setSaving(false)
  }

  const handleSaveSettings = async () => {
    if (!config) return
    setSaving(true); setError(''); setSaved(false)
    try {
      const res: any = await post(API.POST_UPDATE_ENTERPRISE, { id: config.id, ...settingsForm })
      if (res && res.code === 200) { setSaved(true); loadConfig() }
      else { setError(res?.message || t('保存失败', 'Save failed')) }
    } catch { setError(t('网络错误', 'Network error')) }
    setSaving(false)
  }

  const handleChangeRole = async (userId: string, newRole: string) => {
    setRoleUpdating(userId)
    try {
      const res: any = await put(`${API.PUT_ENTERPRISE_USER_ROLE}/${userId}/role?role=${newRole}`, {})
      if (res && res.code === 200) {
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)))
      }
    } catch {}
    setRoleUpdating(null)
  }

  const handleAddMember = async () => {
    if (!addMemberUserId.trim() || !config) return
    setAddMemberLoading(true); setAddMemberMsg('')
    try {
      const res: any = await post(API.POST_BIND_ENTERPRISE, {
        enterpriseId: config.id,
        userId: addMemberUserId.trim(),
      })
      if (res && res.code === 200) {
        setAddMemberMsg(t('添加成功', 'Member added'))
        setAddMemberUserId('')
        loadUsers()
        refreshUserInfo()
      } else {
        setAddMemberMsg(res?.message || t('添加失败', 'Failed to add'))
      }
    } catch {
      setAddMemberMsg(t('网络错误', 'Network error'))
    }
    setAddMemberLoading(false)
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingLogo(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res: any = await axios.post(API.GET_UPLOAD, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        validateStatus: (status: number) => status >= 200 && status < 400,
      })
      if (res.data && res.data.code === 200 && res.data.data) {
        const data = res.data.data
        const logoUrl = data.url || data.file_url || (data.id ? `/file/upload/${data.id}` : '')
        if (logoUrl) setBranding((prev) => ({ ...prev, logoUrl }))
      }
    } catch {}
    setUploadingLogo(false)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const formatTokens = (n: number) => {
    if (!n && n !== 0) return '-'
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
    return `${n}`
  }

  const copyShortCode = () => {
    if (!config?.shortCode) return
    const url = `${window.location.origin}/login?shortCode=${config.shortCode}`
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (!isLoggedIn) return null

  const tabs: { key: TabKey; label: string; labelEn: string; icon: React.ElementType }[] = [
    { key: 'overview', label: '概览', labelEn: 'Overview', icon: Building2 },
    { key: 'branding', label: '品牌定制', labelEn: 'Branding', icon: Palette },
    { key: 'users', label: '成员管理', labelEn: 'Members', icon: Users },
    { key: 'settings', label: '企业设置', labelEn: 'Settings', icon: Settings },
  ]

  const adminCount = users.filter((u) => u.role === 'TENANT_ADMIN').length
  const memberCount = users.filter((u) => u.role !== 'TENANT_ADMIN').length
  const quotaPercent = quota ? Math.min((quota.used / quota.total) * 100, 100) : 0

  return (
    <div className="h-screen flex flex-col bg-[#fafafa]">
      {/* 顶栏 */}
      <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 shrink-0 z-30">
        <Link to="/workspace" className="flex items-center gap-2 text-lg font-semibold mr-2 shrink-0">
          <Logo size={24} />
          <span className="text-[#1a1a1a]">PrimeBook</span>
          <span className="text-orange-500">AI</span>
        </Link>
        <Link to="/workspace" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all mr-4">
          <ArrowLeft className="h-3.5 w-3.5 inline mr-1.5" />
          {t('返回工作台', 'Back')}
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          <Link to="/workspace" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
            {t('对话', 'Chat')}
          </Link>
          <Link to="/knowledge" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
            {t('知识库', 'Knowledge')}
          </Link>
          <button className="px-3 py-1.5 text-[13px] rounded-lg text-[#1a1a1a] font-medium bg-black/[0.04]">
            <Building2 className="h-3.5 w-3.5 inline mr-1.5" />
            {t('企业管理', 'Enterprise')}
          </button>
        </nav>
        <div className="ml-auto" />
      </header>

      <div className="flex flex-1 min-h-0">
        {/* 左侧 */}
        <aside className="hidden md:flex w-56 flex-col border-r border-black/[0.04] bg-white/50">
          {config && (
            <div className="p-3 border-b border-black/[0.04]">
              <div className="flex items-center gap-3 p-2.5 rounded-xl bg-gradient-to-r from-orange-50/80 to-amber-50/50">
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {branding.logoUrl ? (
                    <img src={branding.logoUrl} alt="" className="w-10 h-10 rounded-xl object-cover" />
                  ) : (
                    <Building2 className="h-5 w-5 text-orange-500" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1a1a1a] truncate">{config.name}</p>
                  <p className="text-[11px] text-gray-400">{config.subscriptionTier || 'FREE'}</p>
                </div>
              </div>
            </div>
          )}

          {quota && (
            <div className="px-4 py-3 border-b border-black/[0.04]">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-gray-400">{t('Token 额度', 'Token Quota')}</span>
                <span className="text-[11px] text-gray-500">{formatTokens(quota.used)} / {formatTokens(quota.total)}</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${quotaPercent > 80 ? 'bg-red-500' : quotaPercent > 50 ? 'bg-amber-500' : 'bg-orange-500'}`} style={{ width: `${quotaPercent}%` }} />
              </div>
              <Link to="/pricing" className="mt-2 block">
                <button className="w-full py-1.5 text-[11px] text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors font-medium">
                  {t('升级套餐', 'Upgrade Plan')} →
                </button>
              </Link>
            </div>
          )}

          <div className="flex-1 p-2 space-y-0.5">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setSaved(false); setError('') }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-all ${
                  activeTab === tab.key ? 'bg-orange-50 text-[#1a1a1a] font-medium' : 'text-gray-500 hover:bg-black/[0.03] hover:text-[#1a1a1a]'
                }`}
              >
                <tab.icon className="h-4 w-4 opacity-50" />
                {t(tab.label, tab.labelEn)}
              </button>
            ))}
          </div>
        </aside>

        {/* 主内容区 */}
        <main className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
            </div>
          ) : (
            <div className="max-w-3xl mx-auto px-6 py-8">
              {/* 移动端 Tab */}
              <div className="flex md:hidden gap-1 mb-6 overflow-x-auto pb-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => { setActiveTab(tab.key); setSaved(false); setError('') }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm whitespace-nowrap transition-all ${
                      activeTab === tab.key ? 'bg-orange-50 text-[#1a1a1a] font-medium' : 'text-gray-500 bg-white/60'
                    }`}
                  >
                    <tab.icon className="h-3.5 w-3.5" />
                    {t(tab.label, tab.labelEn)}
                  </button>
                ))}
              </div>

              {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-500 text-xs">{error}</div>}
              {saved && <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-600 text-xs">{t('保存成功', 'Saved successfully')}</div>}

              {/* ===== 概览 ===== */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-[#1a1a1a]">{t('企业概览', 'Overview')}</h2>
                    <p className="text-xs text-gray-400 mt-1">{t('企业运营数据一览', 'Enterprise overview at a glance')}</p>
                  </div>

                  {!config ? (
                    <div className="p-8 rounded-2xl bg-white border border-black/[0.04] text-center">
                      <Building2 className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-sm text-gray-400 mb-1">{t('暂无企业信息', 'No enterprise info')}</p>
                      <p className="text-xs text-gray-300">{t('您可能未绑定企业，或不是企业管理员', 'You may not be bound to an enterprise, or are not an admin')}</p>
                      <Link to="/enterprise-bind" className="inline-block mt-4">
                        <Button variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-50 h-9 text-xs rounded-xl">
                          {t('去绑定企业', 'Bind Enterprise')}
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <>
                      {/* 统计卡片 */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-4 rounded-2xl bg-white border border-black/[0.04]">
                          <p className="text-[11px] text-gray-400 mb-1">{t('成员数', 'Members')}</p>
                          <p className="text-xl font-semibold text-[#1a1a1a]">{users.length}<span className="text-xs text-gray-400 font-normal ml-1">/ {config.maxUsers || '∞'}</span></p>
                        </div>
                        <div className="p-4 rounded-2xl bg-white border border-black/[0.04]">
                          <p className="text-[11px] text-gray-400 mb-1">{t('管理员', 'Admins')}</p>
                          <p className="text-xl font-semibold text-orange-600">{adminCount}</p>
                        </div>
                        <div className="p-4 rounded-2xl bg-white border border-black/[0.04]">
                          <p className="text-[11px] text-gray-400 mb-1">{t('已用Token', 'Tokens Used')}</p>
                          <p className="text-xl font-semibold text-[#1a1a1a]">{formatTokens(quota?.used || config.enterpriseTokenUsed || 0)}</p>
                        </div>
                        <div className="p-4 rounded-2xl bg-white border border-black/[0.04]">
                          <p className="text-[11px] text-gray-400 mb-1">{t('剩余Token', 'Remaining')}</p>
                          <p className="text-xl font-semibold text-emerald-600">{formatTokens(quota?.remaining || 0)}</p>
                        </div>
                      </div>

                      {/* Token 用量进度 */}
                      {quota && (
                        <div className="p-4 rounded-2xl bg-white border border-black/[0.04]">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-[#1a1a1a]">{t('Token 用量', 'Token Usage')}</span>
                            <span className="text-xs text-gray-400">{quotaPercent.toFixed(1)}% {t('已使用', 'used')}</span>
                          </div>
                          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${quotaPercent > 80 ? 'bg-red-500' : quotaPercent > 50 ? 'bg-amber-500' : 'bg-orange-500'}`} style={{ width: `${quotaPercent}%` }} />
                          </div>
                          <div className="flex justify-between mt-2 text-[11px] text-gray-400">
                            <span>{t('已用', 'Used')}: {formatTokens(quota.used)}</span>
                            <span>{t('总量', 'Total')}: {formatTokens(quota.total)}</span>
                          </div>
                        </div>
                      )}

                      {/* 企业信息概要 */}
                      <div className="p-4 rounded-2xl bg-white border border-black/[0.04] space-y-3">
                        <span className="text-sm font-medium text-[#1a1a1a]">{t('企业信息', 'Enterprise Info')}</span>
                        <div className="grid grid-cols-2 gap-y-3 text-xs">
                          <div><span className="text-gray-400">{t('企业名称', 'Name')}</span><p className="text-[#1a1a1a] font-medium mt-0.5">{config.name}</p></div>
                          <div><span className="text-gray-400">{t('行业', 'Industry')}</span><p className="text-[#1a1a1a] mt-0.5">{config.industryType || '-'}</p></div>
                          <div><span className="text-gray-400">{t('套餐', 'Plan')}</span><p className="text-orange-600 font-medium mt-0.5">{config.subscriptionTier || 'FREE'}</p></div>
                          <div><span className="text-gray-400">{t('状态', 'Status')}</span><p className="text-emerald-600 mt-0.5">{config.status || 'ACTIVE'}</p></div>
                          <div><span className="text-gray-400">{t('联系人', 'Contact')}</span><p className="text-[#1a1a1a] mt-0.5">{config.contactName || '-'}</p></div>
                          <div><span className="text-gray-400">{t('短码', 'Short Code')}</span>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-[#1a1a1a] font-mono">{config.shortCode || '-'}</span>
                              {config.shortCode && (
                                <button onClick={copyShortCode} className="p-0.5 rounded text-gray-300 hover:text-orange-500 transition-colors">
                                  {copied ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        {config.shortCode && (
                          <p className="text-[11px] text-gray-400 pt-2 border-t border-black/[0.04]">
                            {t('邀请链接', 'Invite link')}: <span className="font-mono text-orange-500">{window.location.origin}/login?shortCode={config.shortCode}</span>
                          </p>
                        )}
                      </div>

                      {/* 最近成员 */}
                      {users.length > 0 && (
                        <div className="p-4 rounded-2xl bg-white border border-black/[0.04]">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-[#1a1a1a]">{t('最近成员', 'Recent Members')}</span>
                            <button onClick={() => setActiveTab('users')} className="text-xs text-orange-500 hover:text-orange-600 flex items-center gap-0.5">
                              {t('查看全部', 'View All')} <ChevronRight className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="space-y-2">
                            {users.slice(0, 5).map((u) => (
                              <div key={u.id} className="flex items-center gap-2">
                                <Avatar className="h-7 w-7">
                                  {u.avatarUrl && <AvatarImage src={u.avatarUrl} alt={u.nickName} />}
                                  <AvatarFallback className="bg-orange-500 text-white text-[10px]">{u.nickName?.[0] || 'U'}</AvatarFallback>
                                </Avatar>
                                <span className="text-xs text-[#1a1a1a] flex-1 truncate">{u.nickName || u.userName}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${u.role === 'TENANT_ADMIN' ? 'bg-orange-50 text-orange-600' : 'bg-gray-50 text-gray-500'}`}>
                                  {u.role === 'TENANT_ADMIN' ? t('管理员', 'Admin') : t('成员', 'Member')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ===== 品牌定制 ===== */}
              {activeTab === 'branding' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-[#1a1a1a]">{t('品牌定制', 'Branding')}</h2>
                    <p className="text-xs text-gray-400 mt-1">{t('自定义企业品牌外观，让AI管家更贴合企业形象', 'Customize your AI butler to match your brand')}</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">{t('企业Logo', 'Company Logo')}</Label>
                    <div className="flex items-center gap-4">
                      <div className="relative group cursor-pointer" onClick={() => logoInputRef.current?.click()}>
                        <div className="w-20 h-20 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center overflow-hidden">
                          {branding.logoUrl ? <img src={branding.logoUrl} alt="" className="w-20 h-20 object-cover" /> : <Building2 className="h-8 w-8 text-orange-300" />}
                        </div>
                        <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          {uploadingLogo ? <Loader2 className="h-6 w-6 text-white animate-spin" /> : <Upload className="h-5 w-5 text-white" />}
                        </div>
                        <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={handleLogoUpload} />
                      </div>
                      <div className="text-xs text-gray-400">
                        <p>{t('推荐尺寸 200x200px', 'Recommended 200x200px')}</p>
                        <p>{t('支持 PNG, JPG, SVG, WebP', 'PNG, JPG, SVG, WebP')}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{t('企业名称', 'Company Name')}</Label>
                      <Input value={branding.enterpriseName} onChange={(e) => setBranding((p) => ({ ...p, enterpriseName: e.target.value }))} className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{t('企业短码', 'Short Code')}</Label>
                      <Input value={branding.shortCode} onChange={(e) => setBranding((p) => ({ ...p, shortCode: e.target.value }))} placeholder="zhishu" className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">{t('欢迎语', 'Welcome Text')}</Label>
                    <Input value={branding.welcomeText} onChange={(e) => setBranding((p) => ({ ...p, welcomeText: e.target.value }))} placeholder={t('你好，我是AI管家', "Hi, I'm your AI assistant")} className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{t('主色调', 'Primary Color')}</Label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={branding.themePrimaryColor || '#f97316'} onChange={(e) => setBranding((p) => ({ ...p, themePrimaryColor: e.target.value }))} className="w-9 h-9 rounded-lg border border-black/[0.06] cursor-pointer" />
                        <Input value={branding.themePrimaryColor || '#f97316'} onChange={(e) => setBranding((p) => ({ ...p, themePrimaryColor: e.target.value }))} className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06] font-mono" maxLength={7} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{t('辅色调', 'Secondary Color')}</Label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={branding.themeSecondaryColor || '#fb923c'} onChange={(e) => setBranding((p) => ({ ...p, themeSecondaryColor: e.target.value }))} className="w-9 h-9 rounded-lg border border-black/[0.06] cursor-pointer" />
                        <Input value={branding.themeSecondaryColor || '#fb923c'} onChange={(e) => setBranding((p) => ({ ...p, themeSecondaryColor: e.target.value }))} className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06] font-mono" maxLength={7} />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">{t('品牌预览', 'Preview')}</Label>
                    <div className="p-4 rounded-2xl bg-white border border-black/[0.04]">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden" style={{ backgroundColor: `${branding.themePrimaryColor}15` }}>
                          {branding.logoUrl ? <img src={branding.logoUrl} alt="" className="w-10 h-10 rounded-xl object-cover" /> : <Building2 className="h-5 w-5" style={{ color: branding.themePrimaryColor }} />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1a1a1a]">{branding.enterpriseName || '企业名称'}</p>
                          <p className="text-[11px] text-gray-400">{branding.welcomeText || '你好，我是AI管家'}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="h-8 px-4 rounded-xl text-white text-xs flex items-center font-medium" style={{ backgroundColor: branding.themePrimaryColor }}>{t('开始对话', 'Start Chat')}</div>
                        <div className="h-8 px-4 rounded-xl text-xs flex items-center font-medium border" style={{ color: branding.themePrimaryColor, borderColor: `${branding.themePrimaryColor}40`, backgroundColor: `${branding.themePrimaryColor}08` }}>{t('了解更多', 'Learn More')}</div>
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleSaveBranding} className="bg-[#1a1a1a] text-white hover:bg-[#333] h-10 text-sm font-medium rounded-xl px-8" disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    {t('保存品牌设置', 'Save Branding')}
                  </Button>
                </div>
              )}

              {/* ===== 成员管理 ===== */}
              {activeTab === 'users' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-[#1a1a1a]">{t('成员管理', 'Members')}</h2>
                      <p className="text-xs text-gray-400 mt-1">
                        {t(`${adminCount} 位管理员 · ${memberCount} 位成员`, `${adminCount} admins · ${memberCount} members`)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {config?.shortCode && (
                        <Button
                          variant="outline"
                          className="border-orange-200 text-orange-600 hover:bg-orange-50 h-9 text-xs rounded-xl"
                          onClick={copyShortCode}
                        >
                          {copied ? <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                          {t('复制邀请链接', 'Invite Link')}
                        </Button>
                      )}
                      <Button
                        className="bg-[#1a1a1a] text-white hover:bg-[#333] h-9 text-xs rounded-xl"
                        onClick={() => { setShowAddMember(true); setAddMemberMsg('') }}
                      >
                        <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                        {t('添加成员', 'Add Member')}
                      </Button>
                    </div>
                  </div>

                  {/* 添加成员弹窗 */}
                  {showAddMember && (
                    <div className="p-4 rounded-2xl bg-white border border-orange-100 shadow-sm space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-[#1a1a1a]">{t('添加成员到企业', 'Add Member')}</h3>
                        <button onClick={() => setShowAddMember(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
                      </div>
                      <p className="text-[11px] text-gray-400">{t('输入用户的用户ID，将其绑定到当前企业', "Enter the user's ID to bind them to this enterprise")}</p>
                      <div className="flex gap-2">
                        <Input
                          value={addMemberUserId}
                          onChange={(e) => setAddMemberUserId(e.target.value)}
                          placeholder={t('用户ID', 'User ID')}
                          className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]"
                        />
                        <Button
                          onClick={handleAddMember}
                          className="bg-orange-500 text-white hover:bg-orange-600 h-9 text-sm rounded-xl px-4 shrink-0"
                          disabled={addMemberLoading || !addMemberUserId.trim()}
                        >
                          {addMemberLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('添加', 'Add')}
                        </Button>
                      </div>
                      {addMemberMsg && (
                        <p className={`text-xs flex items-center gap-1 ${addMemberMsg.includes('成功') ? 'text-emerald-500' : 'text-red-500'}`}>
                          {addMemberMsg.includes('成功') ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                          {addMemberMsg}
                        </p>
                      )}
                      {config?.shortCode && (
                        <div className="p-3 rounded-xl bg-orange-50/50 border border-orange-100/50">
                          <p className="text-[11px] text-orange-600 font-medium mb-1">{t('或通过邀请链接', 'Or share invite link')}</p>
                          <p className="text-[11px] text-gray-500 font-mono break-all">{window.location.origin}/login?shortCode={config.shortCode}</p>
                          <p className="text-[10px] text-gray-400 mt-1">{t('新用户注册后将自动加入企业', 'New users will auto-join this enterprise')}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 成员列表 */}
                  <div className="space-y-2">
                    {/* 管理员分组 */}
                    {adminCount > 0 && (
                      <div>
                        <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2 px-1">{t('管理员', 'Administrators')}</p>
                        {users.filter((u) => u.role === 'TENANT_ADMIN').map((u) => (
                          <div key={u.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-black/[0.04] mb-2 group">
                            <Avatar className="h-10 w-10">
                              {u.avatarUrl && <AvatarImage src={u.avatarUrl} alt={u.nickName} />}
                              <AvatarFallback className="bg-orange-500 text-white text-xs">{u.nickName?.[0] || u.userName?.[0] || 'U'}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-[#1a1a1a] truncate">{u.nickName || u.userName}</p>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-50 text-orange-600 text-[10px] font-medium">
                                  <Crown className="h-2.5 w-2.5" />{t('管理员', 'Admin')}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-400">
                                {u.phoneNumber && <span className="flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" />{u.phoneNumber}</span>}
                                {u.email && <span className="flex items-center gap-0.5"><Mail className="h-2.5 w-2.5" />{u.email}</span>}
                                {u.department && <span>{u.department}{u.position ? ` · ${u.position}` : ''}</span>}
                              </div>
                            </div>
                            {u.id !== (user as any)?.id && (
                              <button
                                onClick={() => handleChangeRole(u.id, 'USER')}
                                disabled={roleUpdating === u.id}
                                className="p-2 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50 opacity-0 group-hover:opacity-100 transition-all"
                                title={t('降为成员', 'Demote to Member')}
                              >
                                {roleUpdating === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* 普通成员分组 */}
                    {memberCount > 0 && (
                      <div>
                        <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2 px-1">{t('成员', 'Members')}</p>
                        {users.filter((u) => u.role !== 'TENANT_ADMIN').map((u) => (
                          <div key={u.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-black/[0.04] mb-2 group">
                            <Avatar className="h-10 w-10">
                              {u.avatarUrl && <AvatarImage src={u.avatarUrl} alt={u.nickName} />}
                              <AvatarFallback className="bg-gray-400 text-white text-xs">{u.nickName?.[0] || u.userName?.[0] || 'U'}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-[#1a1a1a] truncate">{u.nickName || u.userName}</p>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-50 text-gray-500 text-[10px]">
                                  <Shield className="h-2.5 w-2.5" />{t('成员', 'Member')}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-400">
                                {u.phoneNumber && <span className="flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" />{u.phoneNumber}</span>}
                                {u.email && <span className="flex items-center gap-0.5"><Mail className="h-2.5 w-2.5" />{u.email}</span>}
                                {u.department && <span>{u.department}{u.position ? ` · ${u.position}` : ''}</span>}
                              </div>
                            </div>
                            <button
                              onClick={() => handleChangeRole(u.id, 'TENANT_ADMIN')}
                              disabled={roleUpdating === u.id}
                              className="p-2 rounded-lg text-gray-300 hover:text-orange-500 hover:bg-orange-50 opacity-0 group-hover:opacity-100 transition-all"
                              title={t('设为管理员', 'Promote to Admin')}
                            >
                              {roleUpdating === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ===== 企业设置 ===== */}
              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-[#1a1a1a]">{t('企业设置', 'Settings')}</h2>
                    <p className="text-xs text-gray-400 mt-1">{t('管理企业基本信息和联系方式', 'Manage enterprise info and contacts')}</p>
                  </div>

                  {!config ? (
                    <div className="p-8 rounded-2xl bg-white border border-black/[0.04] text-center">
                      <Settings className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-sm text-gray-400 mb-1">{t('暂无企业信息', 'No enterprise info')}</p>
                      <p className="text-xs text-gray-300">{t('需要企业管理员权限才能查看和修改设置', 'Admin permission required to view and edit settings')}</p>
                      <Link to="/enterprise-bind" className="inline-block mt-4">
                        <Button variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-50 h-9 text-xs rounded-xl">
                          {t('去绑定企业', 'Bind Enterprise')}
                        </Button>
                      </Link>
                    </div>
                  ) : (<>

                  {/* 基本信息 */}
                  <div className="p-5 rounded-2xl bg-white border border-black/[0.04] space-y-4">
                    <p className="text-sm font-medium text-[#1a1a1a]">{t('基本信息', 'Basic Info')}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-500">{t('企业名称', 'Company Name')}</Label>
                        <Input value={settingsForm.name} onChange={(e) => setSettingsForm((p) => ({ ...p, name: e.target.value }))} className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-500">{t('行业类型', 'Industry')}</Label>
                        <Input value={settingsForm.industryType} onChange={(e) => setSettingsForm((p) => ({ ...p, industryType: e.target.value }))} placeholder={t('如：互联网、教育', 'e.g., Internet, Education')} className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]" />
                      </div>
                    </div>
                  </div>

                  {/* 联系信息 */}
                  <div className="p-5 rounded-2xl bg-white border border-black/[0.04] space-y-4">
                    <p className="text-sm font-medium text-[#1a1a1a]">{t('联系信息', 'Contact Info')}</p>
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-500">{t('联系人', 'Contact Name')}</Label>
                        <Input value={settingsForm.contactName} onChange={(e) => setSettingsForm((p) => ({ ...p, contactName: e.target.value }))} className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-gray-500">{t('联系邮箱', 'Contact Email')}</Label>
                          <Input type="email" value={settingsForm.contactEmail} onChange={(e) => setSettingsForm((p) => ({ ...p, contactEmail: e.target.value }))} className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-gray-500">{t('联系电话', 'Contact Phone')}</Label>
                          <Input value={settingsForm.contactPhone} onChange={(e) => setSettingsForm((p) => ({ ...p, contactPhone: e.target.value }))} className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 邀请码设置 */}
                  <div className="p-5 rounded-2xl bg-white border border-black/[0.04] space-y-3">
                    <p className="text-sm font-medium text-[#1a1a1a]">{t('邀请设置', 'Invitation')}</p>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{t('企业短码', 'Short Code')}</Label>
                      <div className="flex items-center gap-2">
                        <Input value={config.shortCode || '-'} disabled className="h-9 text-sm rounded-xl bg-gray-50 border-black/[0.04] font-mono" />
                        {config.shortCode && (
                          <Button variant="outline" className="h-9 px-3 text-xs rounded-xl border-black/[0.06] shrink-0" onClick={copyShortCode}>
                            {copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                          </Button>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400">{t('用户通过 /login?shortCode=xxx 注册后自动加入企业', 'Users auto-join after registering via shortCode link')}</p>
                    </div>
                  </div>

                  {/* 系统信息 */}
                  <div className="p-5 rounded-2xl bg-gray-50/50 border border-black/[0.04] space-y-3">
                    <p className="text-sm font-medium text-gray-400">{t('系统信息', 'System Info')}</p>
                    <div className="grid grid-cols-2 gap-y-3 text-xs">
                      <div><span className="text-gray-400">{t('企业ID', 'Enterprise ID')}</span><p className="text-[#1a1a1a] font-mono mt-0.5 break-all">{config.id}</p></div>
                      <div><span className="text-gray-400">{t('套餐', 'Plan')}</span><p className="text-orange-600 font-medium mt-0.5">{config.subscriptionTier || 'FREE'}</p></div>
                      <div><span className="text-gray-400">{t('最大用户数', 'Max Users')}</span><p className="text-[#1a1a1a] mt-0.5">{config.maxUsers || '-'}</p></div>
                      <div><span className="text-gray-400">{t('状态', 'Status')}</span><p className="text-emerald-600 mt-0.5">{config.status || 'ACTIVE'}</p></div>
                      <div><span className="text-gray-400">{t('Token 总额度', 'Total Quota')}</span><p className="text-[#1a1a1a] mt-0.5">{formatTokens(config.enterpriseTokenQuota)}</p></div>
                      <div><span className="text-gray-400">{t('Token 已用', 'Used')}</span><p className="text-[#1a1a1a] mt-0.5">{formatTokens(config.enterpriseTokenUsed)}</p></div>
                      {config.expireTime && <div><span className="text-gray-400">{t('到期时间', 'Expires')}</span><p className="text-[#1a1a1a] mt-0.5">{new Date(config.expireTime).toLocaleDateString()}</p></div>}
                      {config.createTime && <div><span className="text-gray-400">{t('创建时间', 'Created')}</span><p className="text-[#1a1a1a] mt-0.5">{new Date(config.createTime).toLocaleDateString()}</p></div>}
                    </div>
                  </div>

                  <Button onClick={handleSaveSettings} className="bg-[#1a1a1a] text-white hover:bg-[#333] h-10 text-sm font-medium rounded-xl px-8" disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    {t('保存设置', 'Save Settings')}
                  </Button>
                  </>)}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
